import React, { useState } from 'react';
import {
  Eye,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ArrowUpRight,
  Trash2,
  Zap,
  ScanEye,
  Clock,
  Search,
  Wrench,
  ShieldCheck,
  CircleDot,
  RefreshCw,
  SlidersHorizontal,
} from 'lucide-react';
import type { Statement } from '../types';
import { Button, Tag } from './ui';
import SourcePageViewer from './SourcePageViewer';
import FixGapPanel from './FixGapPanel';
import { money } from '../lib/money';
import { reconciliationBadge, verificationBadge } from '../lib/enums';

interface StatementDetailPanelProps {
  statements: Statement[];
  accountType: string;
  caseId: string;
  onDeleteStatement?: (statementId: number) => void;
  isDeleting?: boolean;
  onNavigateToTransactions?: (statementId: number) => void;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function formatDateFull(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Reconciliation is silent when it succeeds. We only surface an icon/badge for
// a small gap (clay) or a large gap (red); balanced / not-applicable statements
// show nothing.
function getReconIcon(status: string | null) {
  switch (status) {
    case 'minor_gap':
    case 'gap':
    case 'close':
      return <AlertTriangle size={16} className="text-clay-600" />;
    case 'unreconciled':
      return <XCircle size={16} className="text-danger" />;
    default:
      return null;
  }
}

const getReconBadge = reconciliationBadge;

// The running-balance chain proves every individual transaction, not just the
// ending balance. Unlike reconciliation (silent on success), a clean "verified"
// is a positive signal the user wants to see. "partial" / "unverifiable" /
// "not_applicable" show no header badge — they are explained in the detail panel.
const getVerifyBadge = verificationBadge;

// parsing_warnings is a JSON-array string persisted at extraction time. Entries
// are plain-English strings, plus structured objects (e.g. party_mismatch_warning)
// that carry their text in a `message` field. The array also holds verbose
// per-page extraction notes ("Page 1: ...") kept for internal backfills — those
// are not review reasons and must not be shown to the user.
function getReviewReasons(parsingWarnings: string | null): string[] {
  if (!parsingWarnings) return [];
  let entries: unknown[];
  try { entries = JSON.parse(parsingWarnings); } catch { return []; }
  if (!Array.isArray(entries)) return [];
  return entries
    .map((w) => {
      if (typeof w === 'string') return w;
      if (typeof w === 'object' && w !== null) {
        const msg = (w as { message?: unknown }).message;
        if (typeof msg === 'string') return msg;
      }
      return null;
    })
    .filter((m): m is string => !!m)
    .filter((m) => !/^Page \d+\s*:/i.test(m));
}

type GapFix = { fix_type: string; description: string; delta: number };
type GapSuspect = { suspect_type: string; description: string; transaction_id?: number; amount?: number };
type GapResult = {
  loading: boolean;
  status?: string;
  verdict?: string;
  confidence?: string;
  explanation?: string;
  original_gap?: number;
  final_gap?: number;
  fixes_applied?: GapFix[];
  suspects?: GapSuspect[];
  error?: string;
};

function statusColors(status?: string): { bg: string; border: string; text: string; icon: React.ReactNode } {
  switch (status) {
    case 'resolved':
      return { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-900', icon: <ShieldCheck size={12} className="text-green-700" /> };
    case 'within_tolerance':
      return { bg: 'bg-info-soft', border: 'border-info', text: 'text-info', icon: <CheckCircle2 size={12} className="text-info" /> };
    case 'suspects_found':
      return { bg: 'bg-clay-50', border: 'border-clay-200', text: 'text-clay-900', icon: <AlertTriangle size={12} className="text-clay-600" /> };
    case 'unresolved':
      return { bg: 'bg-danger-soft', border: 'border-danger', text: 'text-danger', icon: <XCircle size={12} className="text-danger" /> };
    default:
      return { bg: 'bg-cream', border: 'border-cream-dark', text: 'text-ink', icon: <CircleDot size={12} className="text-ink-faint" /> };
  }
}

function GapResolverResult({ result }: { result: GapResult }) {
  if (result.error) {
    return (
      <div className="mt-2 text-xs bg-danger-soft border border-danger rounded p-2 text-danger flex items-center gap-2">
        <XCircle size={12} /> {result.error}
      </div>
    );
  }

  const colors = statusColors(result.status);
  const confidenceLabel: Record<string, string> = { high: 'High confidence', medium: 'Medium confidence', low: 'Low confidence' };

  return (
    <div className={`mt-2 text-xs ${colors.bg} border ${colors.border} rounded p-3 space-y-2`}>
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-1">
        <div className={`flex items-center gap-2 font-semibold ${colors.text}`}>
          {colors.icon}
          {result.verdict || result.status}
          {result.confidence && (
            <span className="font-normal text-ink-faint ml-1">· {confidenceLabel[result.confidence] ?? result.confidence}</span>
          )}
        </div>
        {result.original_gap !== undefined && result.final_gap !== undefined &&
          result.original_gap !== result.final_gap && (
          <span className="text-ink-faint tabular-nums">
            gap {money(result.original_gap)} → {money(result.final_gap)}
          </span>
        )}
      </div>

      {/* Explanation */}
      {result.explanation && (
        <p className="text-ink-soft leading-relaxed">{result.explanation}</p>
      )}

      {/* Fixes applied */}
      {(result.fixes_applied?.length ?? 0) > 0 && (
        <div>
          <p className="font-semibold text-green-800 mb-1 flex items-center gap-1"><Wrench size={12} /> Fixes applied</p>
          <ul className="space-y-1">
            {result.fixes_applied!.map((f, i) => (
              <li key={i} className="flex items-start gap-1 text-green-900">
                <CheckCircle2 size={12} className="mt-(--space-hair) shrink-0 text-green-600" />
                {f.description}
                {f.delta !== 0 && (
                  <span className="ml-auto tabular-nums shrink-0">{money(f.delta, { signed: true })}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Suspects */}
      {(result.suspects?.length ?? 0) > 0 && (
        <div>
          <p className="font-semibold text-clay-800 mb-1 flex items-center gap-1"><Search size={12} /> Suspects to review</p>
          <ul className="space-y-1">
            {result.suspects!.map((s, i) => (
              <li key={i} className="flex items-start gap-1 text-clay-900">
                <AlertTriangle size={12} className="mt-(--space-hair) shrink-0 text-clay-600" />
                {s.description}
                {s.amount !== undefined && (
                  <span className="ml-auto tabular-nums shrink-0">{money(s.amount)}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function StatementDetailPanel({
  statements,
  accountType,
  caseId,
  onDeleteStatement,
  isDeleting,
  onNavigateToTransactions,
}: StatementDetailPanelProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [viewingPdf, setViewingPdf] = useState<{ statementId: number; sourceFile: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [gapAnalysis, setGapAnalysis] = useState<Record<number, GapResult>>({});
  const [confirmReprocess, setConfirmReprocess] = useState<number | null>(null);
  const [reprocessResult, setReprocessResult] = useState<Record<number, { loading: boolean; jobId?: number; error?: string }>>({});
  const [fixingGapStatement, setFixingGapStatement] = useState<Statement | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState<Record<number, boolean>>({});

  async function runReprocess(statementId: number) {
    setConfirmReprocess(null);
    setReprocessResult(prev => ({ ...prev, [statementId]: { loading: true } }));
    try {
      const res = await fetch(`/api/statements/${statementId}/reprocess`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reprocess failed');
      setReprocessResult(prev => ({ ...prev, [statementId]: { loading: false, jobId: data.job_id } }));
      setGapAnalysis(prev => { const n = { ...prev }; delete n[statementId]; return n; });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed — please try again.';
      setReprocessResult(prev => ({ ...prev, [statementId]: { loading: false, error: msg } }));
    }
  }

  async function runGapResolver(statementId: number) {
    setGapAnalysis(prev => ({ ...prev, [statementId]: { loading: true } }));
    try {
      const res = await fetch(`/api/statements/${statementId}/resolve-gap`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Resolution failed');
      setGapAnalysis(prev => ({ ...prev, [statementId]: { loading: false, ...data } }));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Resolution failed — please try again.';
      setGapAnalysis(prev => ({ ...prev, [statementId]: { loading: false, error: msg } }));
    }
  }

  const sorted = [...statements].sort((a, b) => {
    const dateA = a.statement_end_date || a.statement_start_date || '';
    const dateB = b.statement_end_date || b.statement_start_date || '';
    return dateB.localeCompare(dateA);
  });

  if (sorted.length === 0) {
    return (
      <div className="text-center py-6 text-ink-faint text-sm">
        No statements found for this account.
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <h4 className="t-label text-green-900 mb-3">
        Monthly Statements ({sorted.length})
      </h4>

      <div className="border border-border rounded-lg overflow-hidden">
        {sorted.map((stmt, idx) => {
          const isExpanded = expandedId === stmt.id;
          const reconBadge = getReconBadge(stmt.reconciliation_status);
          const verifyBadge = getVerifyBadge(stmt.transaction_verification_status);
          const hasIssues = (stmt.low_confidence_count || 0) > 0 ||
            (stmt.null_date_count || 0) > 0 ||
            (stmt.zero_amount_count || 0) > 0;

          return (
            <div key={stmt.id} className={idx > 0 ? 'border-t border-border-subtle' : ''}>
              {/* eslint-disable-next-line no-restricted-syntax -- full-width disclosure row: no ui primitive fits this layout */}
              <button
                className="w-full text-left px-4 py-3 hover:bg-cream transition-colors flex items-center gap-3"
                onClick={() => setExpandedId(isExpanded ? null : stmt.id)}
              >
                <ChevronRight
                  size={16}
                  className={`text-ink-faint transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                />
                {getReconIcon(stmt.reconciliation_status)}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {(stmt.statement_end_date || stmt.statement_start_date) ? (
                      <>
                        <span className="t-label text-green-900 truncate">
                          {formatDate(stmt.statement_end_date || stmt.statement_start_date)}
                        </span>
                        <span className="text-xs text-ink-faint truncate hidden sm:inline">
                          {stmt.source_file}
                        </span>
                      </>
                    ) : (
                      <span className="t-label text-green-900 truncate">
                        {stmt.source_file}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right hidden sm:block">
                    <span className="text-xs text-ink-faint">
                      {stmt.transaction_count} txn{stmt.transaction_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {hasIssues && (
                    <AlertTriangle size={16} className="text-clay-500" />
                  )}
                  {stmt.status === 'needs_review' && (
                    <Tag tone="warn" size="sm" icon={AlertTriangle}>
                      Needs Review
                    </Tag>
                  )}
                  {reconBadge && (
                    <Tag tone={reconBadge.variant === 'danger' ? 'danger' : 'warn'}>
                      {reconBadge.label}
                      {stmt.reconciliation_gap !== null && stmt.reconciliation_gap !== 0
                        ? ` — ${money(stmt.reconciliation_gap)}`
                        : ''}
                    </Tag>
                  )}
                  {verifyBadge && (
                    <Tag
                      tone={verifyBadge.variant === 'success' ? 'good' : 'danger'}
                      icon={verifyBadge.variant === 'success' ? ShieldCheck : AlertTriangle}
                    >
                      {verifyBadge.label}
                    </Tag>
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 pt-1 bg-cream space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-surface-card rounded-md p-3 border border-border">
                      <p className="text-xs text-ink-faint mb-1">Period</p>
                      <p className="t-label text-green-900">
                        {formatDateFull(stmt.statement_start_date)} — {formatDateFull(stmt.statement_end_date)}
                      </p>
                    </div>
                    <div className="bg-surface-card rounded-md p-3 border border-border">
                      <p className="text-xs text-ink-faint mb-1">Beginning Balance</p>
                      <p className="t-label text-green-900 tabular-nums">
                        {money(stmt.beginning_balance)}
                      </p>
                    </div>
                    <div className="bg-surface-card rounded-md p-3 border border-border">
                      <p className="text-xs text-ink-faint mb-1">Ending Balance</p>
                      <p className="t-label text-green-900 tabular-nums">
                        {money(stmt.ending_balance)}
                      </p>
                    </div>
                    <div className="bg-surface-card rounded-md p-3 border border-border">
                      <p className="text-xs text-ink-faint mb-1">Transactions</p>
                      <p className="t-label text-green-900 tabular-nums">
                        {stmt.transaction_count}
                      </p>
                    </div>
                    {stmt.bates_start && (
                      <div className="bg-surface-card rounded-md p-3 border border-border">
                        <p className="text-xs text-ink-faint mb-1">Bates</p>
                        <p className="t-ident text-green-900" title="As-produced page labels — the citation for this statement in discovery filings.">
                          {stmt.bates_start}
                          {stmt.bates_end && stmt.bates_end !== stmt.bates_start
                            ? ` – ${stmt.bates_end}`
                            : ''}
                        </p>
                      </div>
                    )}
                  </div>

                  {stmt.extraction_method && (
                    <div>
                      {/* eslint-disable-next-line no-restricted-syntax -- inline disclosure toggle: no ui primitive fits this layout */}
                      <button
                        onClick={() => setAdvancedOpen(prev => ({ ...prev, [stmt.id]: !prev[stmt.id] }))}
                        className="inline-flex items-center gap-1 text-xs text-ink-faint hover:text-ink-soft transition-colors"
                      >
                        <ChevronRight
                          size={12}
                          className={`transition-transform ${advancedOpen[stmt.id] ? 'rotate-90' : ''}`}
                        />
                        Advanced processing details
                      </button>
                      {advancedOpen[stmt.id] && (
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                      <Tag tone="neutral" icon={stmt.extraction_method === 'text' ? Zap : ScanEye}>
                        {stmt.extraction_method === 'text' ? 'Text extraction' : 'Vision extraction'}
                      </Tag>
                      {stmt.processing_metrics && (
                        <>
                          {stmt.processing_metrics.validation_signals ? (
                            stmt.processing_metrics.validation_passed ? (
                              <Tag tone="good" icon={CheckCircle2}>All checks passed</Tag>
                            ) : (
                              <Tag
                                tone="warn"
                                icon={AlertTriangle}
                                title={Object.entries(stmt.processing_metrics.validation_signals)
                                  .filter(([, v]) => !v)
                                  .map(([k]) => k.replace(/_/g, ' '))
                                  .join(', ')}
                              >
                                {Object.values(stmt.processing_metrics.validation_signals).filter(v => !v).length} check(s) flagged
                              </Tag>
                            )
                          ) : null}
                          {stmt.processing_metrics.fallback_triggered && (
                            <Tag tone="warn" icon={AlertTriangle}>
                              {stmt.processing_metrics.sparse_result_retry ? 'Sparse retry' : 'Fallback used'}
                            </Tag>
                          )}
                          {stmt.processing_metrics.bank_specific_prompt_used && (
                            <Tag tone="good">Bank-optimized</Tag>
                          )}
                          {(stmt.processing_metrics.summary_rows_removed ?? 0) > 0 && (
                            <Tag tone="info">
                              {stmt.processing_metrics.summary_rows_removed} summary row(s) filtered
                            </Tag>
                          )}
                          {stmt.processing_metrics.total_time != null && (
                            <span className="inline-flex items-center gap-1 text-xs text-ink-faint">
                              <Clock size={12} />
                              {stmt.processing_metrics.total_time.toFixed(1)}s
                            </span>
                          )}
                        </>
                      )}
                    </div>
                      )}
                    </div>
                  )}

                  {stmt.transaction_verification_detail &&
                    stmt.transaction_verification_status !== 'not_applicable' && (
                    <div className="bg-surface-card rounded-md p-3 border border-border">
                      <p className="text-xs text-ink-faint mb-2">Transaction Verification</p>
                      {(() => {
                        const v = stmt.transaction_verification_detail!;
                        const st = stmt.transaction_verification_status;
                        if (st === 'verified') {
                          return (
                            <div className="flex items-start gap-2 text-xs text-green-800 bg-green-50 border border-green-200 rounded p-2">
                              <ShieldCheck size={12} className="text-green-700 mt-(--space-hair) shrink-0" />
                              <span>{v.explanation}</span>
                            </div>
                          );
                        }
                        if (st === 'broken') {
                          return (
                            <>
                              <div className="flex items-start gap-2 text-xs text-danger bg-danger-soft border border-danger rounded p-2 mb-2">
                                <XCircle size={12} className="text-danger mt-(--space-hair) shrink-0" />
                                <span>{v.explanation}</span>
                              </div>
                              {v.breaks.length > 0 && (
                                <div className="text-xs text-ink-soft tabular-nums bg-cream rounded p-2 space-y-1">
                                  {v.breaks.slice(0, 5).map((b, i) => (
                                    <p key={i} className="tabular-nums">
                                      {b.date ? `${b.date} — ` : ''}{b.description || 'transaction'}: off by {money(b.residual)}
                                      {' '}(expected {money(b.expected_balance)}, got {money(b.actual_balance)})
                                    </p>
                                  ))}
                                </div>
                              )}
                            </>
                          );
                        }
                        // partial / unverifiable
                        return (
                          <div className="flex items-start gap-2 text-xs text-ink-soft bg-cream border border-cream-dark rounded p-2">
                            <CircleDot size={12} className="text-ink-faint mt-(--space-hair) shrink-0" />
                            <span>{v.explanation}</span>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {(stmt.reconciliation_status === 'minor_gap' ||
                    stmt.reconciliation_status === 'gap' ||
                    stmt.reconciliation_status === 'close' ||
                    stmt.reconciliation_status === 'unreconciled') && (
                    <div className="bg-surface-card rounded-md p-3 border border-border">
                      <p className="text-xs text-ink-faint mb-2">Reconciliation</p>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {getReconIcon(stmt.reconciliation_status)}
                        {reconBadge && (
                          <Tag tone={reconBadge.variant === 'danger' ? 'danger' : 'warn'}>{reconBadge.label}</Tag>
                        )}
                        {stmt.reconciliation_gap !== null && (
                          <span className="text-sm text-ink-soft tabular-nums">
                            Gap: {money(stmt.reconciliation_gap)}
                          </span>
                        )}
                        {stmt.reconciliation_gap !== null && Math.abs(stmt.reconciliation_gap) > 0.50 && (
                          <Button
                            size="sm"
                            icon={Wrench}
                            onClick={() => runGapResolver(stmt.id)}
                            loading={gapAnalysis[stmt.id]?.loading}
                            disabled={reprocessResult[stmt.id]?.loading}
                          >
                            {gapAnalysis[stmt.id]?.loading ? 'Resolving...' : 'Resolve Gap'}
                          </Button>
                        )}
                        {stmt.reconciliation_gap !== null && Math.abs(stmt.reconciliation_gap) > 0.50 && (
                          confirmReprocess === stmt.id ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="text-xs text-clay-700 font-medium">Re-scan will wipe & re-extract this statement. Continue?</span>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => runReprocess(stmt.id)}
                                className="!border-clay-400 !text-clay-700 hover:!bg-clay-50"
                              >
                                Yes, Re-scan
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => setConfirmReprocess(null)}
                              >
                                Cancel
                              </Button>
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              variant="secondary"
                              icon={RefreshCw}
                              onClick={() => setConfirmReprocess(stmt.id)}
                              loading={reprocessResult[stmt.id]?.loading}
                              disabled={gapAnalysis[stmt.id]?.loading}
                              className="!border-clay-400 !text-clay-700 hover:!bg-clay-50"
                            >
                              {reprocessResult[stmt.id]?.loading ? 'Queuing...' : 'Re-scan PDF'}
                            </Button>
                          )
                        )}
                      </div>

                      {/* Confidence cue: how this statement was checked */}
                      {stmt.reconciliation_method === 'summary_box' ? (
                        <div className="flex items-start gap-2 text-xs text-green-800 bg-green-50 border border-green-200 rounded p-2 mb-2">
                          <ShieldCheck size={12} className="text-green-700 mt-(--space-hair) shrink-0" />
                          <span>Verified against the printed statement summary — the most reliable check.</span>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2 text-xs text-ink-soft bg-cream border border-cream-dark rounded p-2 mb-2">
                          <CircleDot size={12} className="text-ink-faint mt-(--space-hair) shrink-0" />
                          <span>Basic balance check — this statement has no printed summary box to verify against.</span>
                        </div>
                      )}

                      {/* Plain-language outcome explanation */}
                      {stmt.reconciliation_explanation && (
                        <p className="text-sm text-ink-soft mb-2">{stmt.reconciliation_explanation}</p>
                      )}

                      {stmt.reconciliation_method === 'summary_box' ? (
                        <div className="text-xs text-ink-faint bg-cream rounded p-2 space-y-1">
                          {stmt.summary_previous_balance !== null && (
                            <p className="tabular-nums">Previous balance: {money(stmt.summary_previous_balance)}</p>
                          )}
                          {stmt.summary_payments_credits_total !== null && (
                            <p className="tabular-nums">Payments &amp; credits: {money(stmt.summary_payments_credits_total)}</p>
                          )}
                          {stmt.summary_purchases_total !== null && (
                            <p className="tabular-nums">Purchases: {money(stmt.summary_purchases_total)}</p>
                          )}
                          {stmt.summary_fees_total !== null && (
                            <p className="tabular-nums">Fees: {money(stmt.summary_fees_total)}</p>
                          )}
                          {stmt.summary_interest_total !== null && (
                            <p className="tabular-nums">Interest: {money(stmt.summary_interest_total)}</p>
                          )}
                          {stmt.summary_new_balance !== null && (
                            <p className="tabular-nums">New balance: {money(stmt.summary_new_balance)}</p>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-ink-faint bg-cream rounded p-2 space-y-1">
                          <p className="tabular-nums">Beginning balance: {money(stmt.beginning_balance)}</p>
                          {accountType.startsWith('Credit Card') ? (
                            <p>− Transaction total (charges increase balance) = Expected ending</p>
                          ) : (
                            <p>+ Transaction total = Expected ending</p>
                          )}
                          <p className="tabular-nums">Actual ending: {money(stmt.ending_balance)}</p>
                          {stmt.reconciliation_gap !== null && (
                            <p className={`tabular-nums ${Math.abs(stmt.reconciliation_gap) > 10 ? 'text-danger font-semibold' : 'text-green-700'}`}>
                              Difference: {money(stmt.reconciliation_gap)}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Agentic resolver results */}
                      {gapAnalysis[stmt.id] && !gapAnalysis[stmt.id].loading && (
                        <GapResolverResult result={gapAnalysis[stmt.id]} />
                      )}

                      {/* Re-scan result */}
                      {reprocessResult[stmt.id] && !reprocessResult[stmt.id].loading && (
                        reprocessResult[stmt.id].error ? (
                          <div className="mt-2 text-xs bg-danger-soft border border-danger rounded p-2 text-danger flex items-center gap-2">
                            <XCircle size={12} /> {reprocessResult[stmt.id].error}
                          </div>
                        ) : (
                          <div className="mt-2 text-xs bg-clay-50 border border-clay-200 rounded p-2 text-clay-900 flex items-start gap-2">
                            <RefreshCw size={12} className="mt-(--space-hair) shrink-0 text-clay-600" />
                            <span>
                              Re-scan queued as job #{reprocessResult[stmt.id].jobId}. Switch to the <strong>Upload</strong> tab to track progress — this statement will update automatically when complete.
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  )}

                  {stmt.status === 'needs_review' && (() => {
                    // Prefer the structured reasons recorded at extraction time
                    // (message + suggested next step); fall back to the persisted
                    // free-text warnings, then to metric heuristics for old rows.
                    const structured = stmt.processing_metrics?.review_reasons ?? [];
                    if (structured.length > 0) {
                      return (
                        <div className="bg-clay-50 rounded-md p-3 border border-clay-200">
                          <p className="text-xs font-medium text-clay-800 mb-2 flex items-center gap-1">
                            <AlertTriangle size={12} />
                            Needs Manual Review
                          </p>
                          <div className="space-y-2">
                            {structured.map((r, i) => (
                              <div key={i} className="text-xs">
                                <p className={`flex items-start gap-1 ${r.severity === 'high' ? 'text-danger' : 'text-clay-800'}`}>
                                  {r.severity === 'high'
                                    ? <XCircle size={12} className="mt-(--space-hair) shrink-0 text-danger" />
                                    : <AlertTriangle size={12} className="mt-(--space-hair) shrink-0 text-clay-600" />}
                                  <span>{r.message}</span>
                                </p>
                                <p className="text-clay-600 mt-1 ml-4">
                                  <span className="font-medium">Next step:</span> {r.suggested_action}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    const reasons = getReviewReasons(stmt.parsing_warnings);
                    const fallbacks: string[] = [];
                    if (reasons.length === 0) {
                      if (stmt.processing_metrics?.zero_transaction_result) {
                        fallbacks.push('No transactions were extracted — please review the original PDF and add transactions manually, or re-upload a higher-quality scan.');
                      }
                      if (stmt.processing_metrics?.reconciliation_gap != null && Math.abs(stmt.processing_metrics.reconciliation_gap) > 10) {
                        fallbacks.push(`Reconciliation gap of ${money(Math.abs(stmt.processing_metrics.reconciliation_gap))} — some transactions may be missing.`);
                      }
                      if (fallbacks.length === 0) {
                        fallbacks.push('Review required — please inspect the original PDF.');
                      }
                    }
                    const items = reasons.length > 0 ? reasons : fallbacks;
                    return (
                      <div className="bg-clay-50 rounded-md p-3 border border-clay-200">
                        <p className="text-xs font-medium text-clay-800 mb-1 flex items-center gap-1">
                          <AlertTriangle size={12} />
                          Needs Manual Review
                        </p>
                        <ul className="text-xs text-clay-700 space-y-1 list-disc list-inside">
                          {items.map((r, i) => (
                            <li key={i}>{r}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}

                  {hasIssues && (
                    <div className="bg-clay-50 rounded-md p-3 border border-clay-200">
                      <p className="text-xs font-medium text-clay-800 mb-2">Data Quality Flags</p>
                      <div className="flex flex-wrap gap-2">
                        {(stmt.low_confidence_count || 0) > 0 && (
                          <Tag tone="warn" icon={AlertTriangle}>
                            {stmt.low_confidence_count} low confidence
                          </Tag>
                        )}
                        {(stmt.null_date_count || 0) > 0 && (
                          <Tag tone="warn" icon={AlertTriangle}>
                            {stmt.null_date_count} missing date{stmt.null_date_count !== 1 ? 's' : ''}
                          </Tag>
                        )}
                        {(stmt.zero_amount_count || 0) > 0 && (
                          <Tag tone="warn" icon={AlertTriangle}>
                            {stmt.zero_amount_count} zero amount{stmt.zero_amount_count !== 1 ? 's' : ''}
                          </Tag>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2 flex-wrap">
                    {(stmt.reconciliation_status === 'unreconciled' ||
                      stmt.reconciliation_status === 'minor_gap' ||
                      stmt.reconciliation_status === 'gap' ||
                      stmt.reconciliation_status === 'close') && (
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={SlidersHorizontal}
                        onClick={() => setFixingGapStatement(stmt)}
                        className="!border-clay-400 !text-clay-700 hover:!bg-clay-50"
                      >
                        Fix Gap
                      </Button>
                    )}
                    {stmt.has_pdf && (
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={Eye}
                        onClick={() => setViewingPdf({ statementId: stmt.id, sourceFile: stmt.source_file })}
                      >
                        View Original
                      </Button>
                    )}
                    {onNavigateToTransactions && (
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={ArrowUpRight}
                        onClick={() => onNavigateToTransactions(stmt.id)}
                      >
                        View Transactions
                      </Button>
                    )}
                    {onDeleteStatement && (
                      <>
                        {confirmDelete === stmt.id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-danger">Delete this statement and its transactions?</span>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                onDeleteStatement(stmt.id);
                                setConfirmDelete(null);
                              }}
                              loading={isDeleting}
                              className="!text-danger !border-danger hover:!bg-danger-soft"
                            >
                              Yes, Delete
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setConfirmDelete(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="secondary"
                            icon={Trash2}
                            onClick={() => setConfirmDelete(stmt.id)}
                            className="!text-danger hover:!bg-danger-soft"
                          >
                            Delete
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {viewingPdf && (
        <SourcePageViewer
          statementId={viewingPdf.statementId}
          pageNumber={1}
          sourceFile={viewingPdf.sourceFile}
          isOpen={true}
          onClose={() => setViewingPdf(null)}
        />
      )}

      {fixingGapStatement && (
        <FixGapPanel
          statement={fixingGapStatement}
          caseId={caseId}
          onClose={() => setFixingGapStatement(null)}
        />
      )}
    </div>
  );
}
